import { GameState, CardData, Position, QueueCard } from '../types/types';
import { v4 as uuidv4 } from 'uuid';
import { TOTAL_CARD_TYPES } from './cardConstants';

// Card dimensions
const CARD_WIDTH = 80;
const CARD_HEIGHT = 80;
const CARD_OVERLAP = 40; // Increased overlap (half a card now)

// Define the number of cards per layer - increasing for more complexity
const LAYER_CARD_COUNTS = [16, 12, 10, 8];

// Define queue sizes
const QUEUE_SIZE = 10;

// Function to generate a new random card
const generateRandomCard = (layer: number, position: Position): CardData => {
  return {
    id: uuidv4(),
    type: Math.floor(Math.random() * TOTAL_CARD_TYPES),
    position,
    layer,
    visible: layer === 0, // Only the bottom layer cards are initially visible
    selected: false,
    blockedBy: []
  };
};

// Function to generate random cards for the queue
const generateQueueCards = (count: number): QueueCard[] => {
  return Array(count).fill(null).map(() => ({
    id: `queue-${uuidv4()}`,
    type: Math.floor(Math.random() * TOTAL_CARD_TYPES)
  }));
};

// Function to check if two card positions would overlap with quarter-card precision
const doCardsOverlap = (pos1: Position, pos2: Position): boolean => {
  // Adjusted overlap detection to allow for quarter-card overlaps
  // Two cards overlap if their centers are closer than 3/4 of card width/height
  const center1 = {
    x: pos1.x + CARD_WIDTH / 2,
    y: pos1.y + CARD_HEIGHT / 2
  };
  
  const center2 = {
    x: pos2.x + CARD_WIDTH / 2,
    y: pos2.y + CARD_HEIGHT / 2
  };
  
  // Calculate the distance between centers
  const dx = Math.abs(center1.x - center2.x);
  const dy = Math.abs(center1.y - center2.y);
  
  // If the distance is less than the width/height minus the allowed overlap, they overlap
  return dx < (CARD_WIDTH - CARD_OVERLAP/2) && dy < (CARD_HEIGHT - CARD_OVERLAP/2);
};

// Function to find cards that block the given position
const findBlockingCards = (
  position: Position,
  layer: number,
  allCards: CardData[]
): string[] => {
  // Cards in the layer above can block this card
  const possibleBlockers = allCards.filter(card => card.layer === layer + 1);
  
  return possibleBlockers
    .filter(card => doCardsOverlap(position, card.position))
    .map(card => card.id);
};

// Function to generate a level with more complex overlapping patterns
export const generateLevel = (): GameState => {
  const gameWidth = 320;  // Width of the game area
  const gameHeight = 480; // Height of the game area
  
  let allCards: CardData[] = [];
  
  // Generate cards for each layer
  for (let layer = 0; layer < LAYER_CARD_COUNTS.length; layer++) {
    const cardCount = LAYER_CARD_COUNTS[layer];
    const layerCards: CardData[] = [];
    
    // Create a more complex grid pattern with various offsets
    const gridCols = 4;
    const gridRows = 4;
    const cellWidth = gameWidth / gridCols;
    const cellHeight = gameHeight / gridRows;
    
    // Generate positions for this layer
    for (let i = 0; i < cardCount; i++) {
      let position: Position;
      let attempts = 0;
      const maxAttempts = 20; // Prevent infinite loops
      
      do {
        // Choose a random grid cell
        const row = Math.floor(Math.random() * gridRows);
        const col = Math.floor(Math.random() * gridCols);
        
        // Add complex offset within cell - allow for quarter-card overlaps
        // This creates a more natural staggered layout with cards partially overlapping
        const offsetX = Math.random() * (cellWidth - CARD_WIDTH * 0.75);
        const offsetY = Math.random() * (cellHeight - CARD_HEIGHT * 0.75);
        
        position = {
          x: col * cellWidth + offsetX,
          y: row * cellHeight + offsetY
        };
        
        // Ensure cards don't overlap too much in the same layer
        attempts++;
      } while (
        layerCards.some(existingCard => {
          const existingPos = existingCard.position;
          // If centers are too close, try a new position
          const centerDist = Math.sqrt(
            Math.pow(position.x + CARD_WIDTH/2 - (existingPos.x + CARD_WIDTH/2), 2) +
            Math.pow(position.y + CARD_HEIGHT/2 - (existingPos.y + CARD_HEIGHT/2), 2)
          );
          // Allow some overlap but not complete
          return centerDist < CARD_WIDTH * 0.4;
        }) && attempts < maxAttempts
      );
      
      // Create the card with this position
      const newCard = generateRandomCard(layer, position);
      layerCards.push(newCard);
    }
    
    // Add cards to the overall array
    allCards = [...allCards, ...layerCards];
  }
  
  // Now determine which cards block which
  for (let i = 0; i < allCards.length; i++) {
    const card = allCards[i];
    
    // Only cards not in the top layer can be blocked
    if (card.layer < LAYER_CARD_COUNTS.length - 1) {
      card.blockedBy = findBlockingCards(card.position, card.layer, allCards);
      
      // A card is visible only if nothing blocks it
      card.visible = card.blockedBy.length === 0;
    }
  }
  
  console.log(`Generated ${allCards.length} cards, ${allCards.filter(c => c.visible).length} initially visible`);
  
  // Make sure we have 3 of each card type to ensure the level is solvable
  // Count how many of each type we have
  const typeCounts: Record<number, number> = {};
  allCards.forEach(card => {
    typeCounts[card.type] = (typeCounts[card.type] || 0) + 1;
  });
  
  // Create sets of 3 for each type
  for (let type = 0; type < TOTAL_CARD_TYPES; type++) {
    if (!typeCounts[type]) typeCounts[type] = 0;
    
    // Add cards to make sure we have at least 3 of each type
    while (typeCounts[type] < 3) {
      // Find a random card to replace
      const randomIndex = Math.floor(Math.random() * allCards.length);
      allCards[randomIndex].type = type;
      typeCounts[type]++;
    }
  }
  
  // Generate the left and right queue cards with a limited number
  const leftQueue = generateQueueCards(QUEUE_SIZE);
  const rightQueue = generateQueueCards(QUEUE_SIZE);
  
  return {
    cards: allCards,
    slots: [],
    leftQueue,
    rightQueue,
    isGameOver: false,
    isGameWon: false,
    revivalChances: 3, // Start with 3 revival chances
    lastMove: null,
    canRevert: false,
    usedRevert: false,
    usedReshuffle: false,
    usedMoveOut: false
  };
}; 